import type { CmmdMember, CmmdProject, CmmdProjectAgentMember } from "@cmmd-forge/contracts";
import { useQuery } from "@tanstack/react-query";

import { createCmmdApiClient } from "~/environments/cmmdApi";
import {
  resolveForgeExecutionTarget,
  type ForgeExecutionTarget,
} from "../projects/forgeExecutionTarget";

const api = createCmmdApiClient();

/**
 * How often the panel re-reads CMMD while it is open.
 *
 * The panel exists so a user can watch an agent work the task, so a stale card
 * is the failure mode that matters. CMMD has no push channel for task mutations
 * today, so the surface polls; ten seconds is fast enough to read as live
 * without turning an idle sidebar into a request loop.
 */
export const CHAT_TASK_PANEL_REFRESH_MS = 10_000;

/** The CMMD anchor a chat thread carries, narrowed to what this panel can use. */
export interface ChatTaskPanelAnchor {
  readonly anchorKind: string | null;
  readonly anchorCmmdId: string | null;
}

export interface ChatTaskPanelContext {
  /** Project that owns the surface, as the string id the task hooks expect. */
  readonly projectId: string | null;
  readonly taskId: number | null;
  /** Full project record, required by the work-session launcher. */
  readonly project: CmmdProject | null;
  /**
   * The Forge environment/project this CMMD project executes in, or `null` when
   * it has no linked Forge project. This is the ref that makes sidebar thread
   * summaries — and therefore task thread history — resolvable here.
   */
  readonly forgeTarget: ForgeExecutionTarget | null;
  readonly projectTitle: string;
  readonly projectIcon: string | null;
  readonly projectIconUrl: string | null;
  readonly members: readonly CmmdMember[];
  readonly agentMembers: readonly CmmdProjectAgentMember[];
  readonly agentMembersLoading: boolean;
  readonly agentMembersError: string | null;
  readonly loading: boolean;
  readonly error: string | null;
}

const EMPTY_MEMBERS: readonly CmmdMember[] = [];
const EMPTY_AGENT_MEMBERS: readonly CmmdProjectAgentMember[] = [];

/** What a thread's anchor names, before any network read. */
export interface ChatTaskPanelAnchorIds {
  readonly taskId: number | null;
  /** Known up front only for a project anchor; a task anchor must be fetched. */
  readonly projectId: string | null;
}

/**
 * Read the panel's starting ids straight off the thread anchor.
 *
 * Split out from the hook because the branch matters and is worth testing
 * without a query client: only `task` yields a task id, only `project` yields a
 * project id up front, and every other anchor kind (`scratch`, and anything
 * added later) resolves to neither rather than being coerced into one.
 */
export function resolveChatTaskPanelAnchor(anchor: ChatTaskPanelAnchor): ChatTaskPanelAnchorIds {
  if (anchor.anchorKind === "task") {
    return { taskId: parsePositiveId(anchor.anchorCmmdId), projectId: null };
  }
  if (anchor.anchorKind === "project") {
    return { taskId: null, projectId: anchor.anchorCmmdId };
  }
  return { taskId: null, projectId: null };
}

/**
 * Resolve a chat thread's CMMD anchor into the ids and people the task panel
 * renders from.
 *
 * A thread anchors to either a task or a project. A task anchor is the richer
 * case and the one the packet's first golden path covers: the task id alone
 * does not name a project, so the task detail is fetched first purely to learn
 * `projectId`, and the project-scoped reads hang off that.
 */
export function useChatTaskPanelContext(anchor: ChatTaskPanelAnchor): ChatTaskPanelContext {
  const { taskId: anchoredTaskId, projectId: anchoredProjectId } =
    resolveChatTaskPanelAnchor(anchor);

  const taskQuery = useQuery({
    queryKey: ["cmmd", "chat-task-panel", "task", anchoredTaskId],
    queryFn: () => api.getForgeTaskDetail(anchoredTaskId as number),
    enabled: anchoredTaskId !== null,
    staleTime: CHAT_TASK_PANEL_REFRESH_MS,
  });

  const resolvedProjectId =
    anchoredProjectId ??
    (taskQuery.data?.projectId != null ? String(taskQuery.data.projectId) : null);

  const projectQuery = useQuery({
    queryKey: ["cmmd", "chat-task-panel", "project", resolvedProjectId],
    queryFn: () => loadPanelProject(resolvedProjectId as string),
    enabled: resolvedProjectId !== null,
    staleTime: 60_000,
  });

  const project = projectQuery.data ?? null;
  return {
    projectId: resolvedProjectId,
    taskId: anchoredTaskId,
    project: project?.project ?? null,
    forgeTarget: project?.forgeTarget ?? null,
    projectTitle: project?.project.title ?? "Project",
    projectIcon: project?.project.icon ?? null,
    projectIconUrl: project?.project.iconUrl ?? null,
    members: project?.members ?? EMPTY_MEMBERS,
    agentMembers: project?.agentMembers ?? EMPTY_AGENT_MEMBERS,
    agentMembersLoading: projectQuery.isPending && resolvedProjectId !== null,
    agentMembersError: project?.agentMembersError ?? null,
    loading: taskQuery.isPending && anchoredTaskId !== null,
    error: resolveError(taskQuery.error, projectQuery.error),
  };
}

interface PanelProject {
  readonly project: CmmdProject;
  readonly forgeTarget: ForgeExecutionTarget | null;
  readonly members: readonly CmmdMember[];
  readonly agentMembers: readonly CmmdProjectAgentMember[];
  readonly agentMembersError: string | null;
}

/**
 * Read only the project facts the detail card and board need.
 *
 * Deliberately not `useCmmdProjectHome`: that hook additionally fetches
 * repository/execution status, project updates and agent events, which this
 * sidebar never renders. Six requests for three fields would be paid on every
 * panel open.
 *
 * Agent members degrade rather than fail the panel — the detail card renders a
 * dedicated error for them, and an owner list that cannot load is not a reason
 * to hide the task.
 */
async function loadPanelProject(projectId: string): Promise<PanelProject> {
  const [project, members, agentMembers, forgeTarget] = await Promise.all([
    api.getProject(projectId),
    api.getProjectMembers(projectId).catch(() => EMPTY_MEMBERS),
    api
      .listProjectAgentMembers(projectId)
      .then((rows) => ({ rows, error: null as string | null }))
      .catch((cause: unknown) => ({ rows: EMPTY_AGENT_MEMBERS, error: messageOf(cause) })),
    // Fails soft: a project with no linked Forge project is legitimate, and a
    // repository read that errors must not take the whole panel down with it.
    resolveForgeExecutionTarget(projectId).catch(() => null),
  ]);
  return {
    project,
    forgeTarget,
    members,
    agentMembers: agentMembers.rows,
    agentMembersError: agentMembers.error,
  };
}

function parsePositiveId(value: string | null): number | null {
  if (value === null || !/^\d+$/.test(value)) return null;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
}

function resolveError(taskError: unknown, projectError: unknown): string | null {
  if (taskError) return messageOf(taskError);
  if (projectError) return messageOf(projectError);
  return null;
}

function messageOf(cause: unknown): string {
  return cause instanceof Error ? cause.message : "Failed to load task context.";
}
